import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
    FolderKanban,
    BarChart3,
    Mail,
    LogOut,
    User,
    Menu,
    X
} from 'lucide-react';

const navItems = [
    { path: '/', label: 'Progetti', icon: FolderKanban },
    { path: '/statistics', label: 'Statistiche', icon: BarChart3 },
    { path: '/gmail', label: 'Email', icon: Mail },
];

export const Layout = ({ children }) => {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Top Navigation */}
            <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        {/* Logo */}
                        <Link to="/" className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-slate-900 rounded-sm flex items-center justify-center">
                                <FolderKanban className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-black text-xl tracking-tight text-slate-900 hidden sm:block" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                Catalogo Progetti
                            </span>
                        </Link>

                        {/* Desktop Navigation */}
                        <nav className="hidden md:flex items-center gap-1">
                            {navItems.map((item) => {
                                const Icon = item.icon;
                                const isActive = location.pathname === item.path;
                                return (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                                        className={`
                                            flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium
                                            transition-all duration-200
                                            ${isActive 
                                                ? 'bg-slate-900 text-white' 
                                                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                                            }
                                        `}
                                    >
                                        <Icon className="w-4 h-4" />
                                        {item.label}
                                    </Link>
                                );
                            })}
                        </nav>

                        {/* User Menu */}
                        <div className="flex items-center gap-4">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button 
                                        variant="ghost" 
                                        className="flex items-center gap-2"
                                        data-testid="user-menu-trigger"
                                    >
                                        <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                                            <User className="w-4 h-4 text-slate-600" />
                                        </div>
                                        <span className="hidden sm:block text-sm font-medium text-slate-700">
                                            {user?.name}
                                        </span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                    <div className="px-2 py-1.5">
                                        <p className="text-sm font-medium">{user?.name}</p>
                                        <p className="text-xs text-slate-500">{user?.email}</p>
                                    </div>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                        onClick={handleLogout}
                                        className="text-red-600 cursor-pointer"
                                        data-testid="logout-btn"
                                    >
                                        <LogOut className="w-4 h-4 mr-2" />
                                        Esci
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>

                            {/* Mobile Menu Button */}
                            <Button
                                variant="ghost"
                                size="icon"
                                className="md:hidden"
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                data-testid="mobile-menu-toggle"
                            >
                                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Mobile Navigation */}
                {mobileMenuOpen && (
                    <div className="md:hidden border-t border-slate-200 bg-white animate-slide-up">
                        <nav className="px-4 py-2 space-y-1">
                            {navItems.map((item) => {
                                const Icon = item.icon;
                                const isActive = location.pathname === item.path;
                                return (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className={`
                                            flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium
                                            ${isActive 
                                                ? 'bg-slate-900 text-white' 
                                                : 'text-slate-600 hover:bg-slate-100'
                                            }
                                        `}
                                    >
                                        <Icon className="w-4 h-4" />
                                        {item.label}
                                    </Link>
                                );
                            })}
                        </nav>
                    </div>
                )}
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="animate-fade-in">
                    {children}
                </div>
            </main>
        </div>
    );
};

export default Layout;
